import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GH } from '../theme';
import { useWindowWidth } from '../hooks/useWindowWidth';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { Reveal } from '../components/Reveal';
import { SectionLabel } from '../components/SectionLabel';
import { GHButton } from '../components/GHButton';
import { ImagePlaceholder } from '../components/ImagePlaceholder';

const SERVICE_AREAS = [
  'Auckland CBD', 'North Shore', 'West Auckland',
  'South Auckland', 'East Auckland', 'Manukau',
  'Waitakere', 'Franklin', 'Rodney',
];

function validateAttachment(file) {
  const ALLOWED_MIME = ['image/jpeg','image/png','image/webp','image/gif','application/pdf'];
  const ALLOWED_EXT  = ['.jpg','.jpeg','.png','.webp','.gif','.pdf'];
  const BLOCKED_EXT  = /\.(exe|bat|sh|php|js|mjs|cjs|ts|cmd|ps1|vbs|reg|dll|sys|dmg|app|jar|zip|rar|7z|gz|tar|iso|bin|msi|scr|pif|com|lnk|hta|wsf|wsh|cpl|inf|msp|mst|ade|adp|xls|xlt|xlm|doc|dot|ppt|pot|rtf|xml|mdb|accdb|chm|hlp)$/i;
  const MAX_SIZE = 5 * 1024 * 1024;
  if (BLOCKED_EXT.test(file.name)) return 'File type not permitted for security reasons.';
  const ext = '.' + file.name.split('.').pop().toLowerCase();
  if (!ALLOWED_EXT.includes(ext)) return 'Only JPG, PNG, WebP, GIF and PDF files are accepted.';
  if (!ALLOWED_MIME.includes(file.type)) return 'Invalid file type. Please attach images or PDFs only.';
  if (file.size > MAX_SIZE) return 'File exceeds 5 MB limit. Please compress or split the file.';
  if ((file.name.match(/\./g) || []).length > 1) {
    const parts = file.name.split('.');
    const secondExt = '.' + parts[parts.length - 2].toLowerCase();
    if (BLOCKED_EXT.test(secondExt)) return 'Suspicious file name detected.';
  }
  return null;
}

export default function ContactPage({ navigate }) {
  const w = useWindowWidth();
  const mob = w < 768;
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', service: '', message: '' });
  const [attachments, setAttachments] = useState([]);
  const [attachError, setAttachError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [focused, setFocused] = useState(null);
  const fileRef = useRef(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handleFiles = (e) => {
    setAttachError('');
    const incoming = Array.from(e.target.files || []);
    const combined = [...attachments];
    let err = '';
    for (const f of incoming) {
      if (combined.length >= 3) { err = 'Maximum 3 attachments allowed.'; break; }
      const problem = validateAttachment(f);
      if (problem) { err = problem; break; }
      combined.push(f);
    }
    if (err) setAttachError(err);
    else setAttachments(combined);
    e.target.value = '';
  };
  const removeFile = (i) => setAttachments(a => a.filter((_, idx) => idx !== i));
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      attachments.forEach(f => fd.append('attachments', f));
      const res = await fetch('/api/contact', { method: 'POST', body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || 'Something went wrong.');
      }
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };
  const inputStyle = (key) => ({
    width: '100%', boxSizing: 'border-box',
    background: focused === key ? 'rgba(22,28,42,0.045)' : 'rgba(22,28,42,0.025)',
    border: `1px solid ${focused === key ? GH.colors.accent : GH.colors.borderDk}`,
    borderRadius: 2, padding: '14px 18px',
    fontFamily: GH.fonts.body, fontSize: 14, color: GH.colors.white,
    outline: 'none', transition: 'all 0.2s ease',
  });
  const labelStyle = { fontFamily: GH.fonts.body, fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: GH.colors.greyLight, display: 'block', marginBottom: 8 };

  return (
    <div style={{ background: GH.colors.bg, minHeight: '100vh' }}>
      {/* Contact Banner */}
      <div style={{ background: GH.colors.gold, padding: mob ? '10px 20px' : '12px 40px', textAlign: 'center' }}>
        <span style={{ fontFamily: GH.fonts.body, fontWeight: 600, fontSize: mob ? 13 : 14, color: '#fff' }}>
          Get in touch. Call us: <strong>020 4198 1876</strong>
        </span>
      </div>
      {/* Page Hero */}
      <div style={{ background: 'linear-gradient(160deg, #1C1F23 0%, #2A2D31 100%)', padding: mob ? '130px 20px 64px' : '140px 40px 80px', position: 'relative', overflow: 'hidden' }}>
        <div className="hero-grid" style={{ position: 'absolute', inset: 0, opacity: 0.4 }} />
        <div style={{ maxWidth: 1300, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Reveal>
            <SectionLabel>Contact Us</SectionLabel>
            <h1 style={{ fontFamily: GH.fonts.display, fontWeight: 800, fontSize: mob ? 'clamp(32px, 8vw, 48px)' : 'clamp(44px, 5.5vw, 72px)', color: '#fff', letterSpacing: '-0.035em', lineHeight: 1.02, margin: '24px 0 20px' }}>
              Let's Talk About<br />
              <span style={{ color: GH.colors.gold }}>Your Property</span>
            </h1>
            <p style={{ fontFamily: GH.fonts.body, fontSize: mob ? 15 : 16, color: GH.colors.onDark.body, lineHeight: 1.65, maxWidth: 500, margin: 0 }}>
              We respond to all enquiries as soon as possible. For urgent response, please give us a call.
            </p>
          </Reveal>
        </div>
      </div>
      {/* Main Content */}
      <div style={{ padding: mob ? '48px 20px 80px' : '80px 40px 120px' }}>
        <div style={{ maxWidth: 1300, margin: '0 auto', display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1.4fr', gap: mob ? 48 : 80, alignItems: 'start' }}>
          {/* Left — Contact Info */}
          <div>
            <Reveal>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, background: GH.colors.borderDk, marginBottom: 48 }}>
                {[
                  { label: 'Phone', value: '020 4198 1876', sub: 'Mon–Fri 7am–6pm' },
                  { label: 'Email', value: 'info@greyholm.co.nz', sub: '' },
                  { label: 'Office', value: 'Hillpark, Auckland', sub: '' },
                  { label: 'Hours', value: 'Mon–Sat 7am–5pm', sub: '' },
                ].map((c, i) => (
                  <div key={i} style={{ background: GH.colors.card, padding: mob ? '20px 20px' : '24px 28px', borderLeft: `3px solid ${i === 0 ? GH.colors.accent : 'transparent'}` }}>
                    <div style={{ fontFamily: GH.fonts.body, fontSize: 10, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: GH.colors.accent, marginBottom: 6 }}>{c.label}</div>
                    <div style={{ fontFamily: GH.fonts.display, fontWeight: 700, fontSize: mob ? 15 : 16, color: GH.colors.white, letterSpacing: '-0.015em' }}>{c.value}</div>
                    {c.sub && <div style={{ fontFamily: GH.fonts.body, fontSize: 12, color: GH.colors.mutedDk, marginTop: 4 }}>{c.sub}</div>}
                  </div>
                ))}
              </div>
            </Reveal>
            {/* Map */}
            <Reveal delay={100}>
              <div style={{ fontFamily: GH.fonts.body, fontSize: 12, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: GH.colors.mutedDk, marginBottom: 12 }}>Service Areas — Greater Auckland</div>
              <div style={{ width: '100%', height: mob ? 200 : 260, overflow: 'hidden', borderRadius: 2, position: 'relative' }}>
                <img src="/images/image10.jpeg" alt="Auckland map — service coverage" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={e => { e.currentTarget.style.display='none'; }} />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(28,31,35,0.6) 0%, transparent 50%)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: 16, left: 16, fontFamily: GH.fonts.display, fontWeight: 700, fontSize: 14, color: '#fff', letterSpacing: '-0.01em' }}>Greater Auckland Region</div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
                {SERVICE_AREAS.map(area => (
                  <span key={area} style={{ fontFamily: GH.fonts.body, fontSize: 12, fontWeight: 500, color: GH.colors.subDk, background: GH.colors.card, border: `1px solid ${GH.colors.borderDk}`, padding: '5px 12px', borderRadius: 1 }}>{area}</span>
                ))}
              </div>
            </Reveal>
          </div>
          {/* Right — Contact Form */}
          <Reveal delay={120}>
            <div style={{ background: GH.colors.card, border: `1px solid ${GH.colors.borderDk}`, borderRadius: 2, padding: mob ? '32px 24px' : '48px 48px' }}>
              {submitted ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <div style={{ width: 56, height: 56, background: GH.colors.accentMuted, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 24 }}>✓</div>
                  <h3 style={{ fontFamily: GH.fonts.display, fontWeight: 700, fontSize: 24, color: GH.colors.white, letterSpacing: '-0.02em', margin: '0 0 12px' }}>Message Sent</h3>
                  <p style={{ fontFamily: GH.fonts.body, fontSize: 15, color: GH.colors.mutedDk, lineHeight: 1.65, margin: '0 0 28px' }}>Thanks for reaching out. Our team will be in touch soon.</p>
                  <GHButton onClick={() => setSubmitted(false)}>Send Another Message</GHButton>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <h2 style={{ fontFamily: GH.fonts.display, fontWeight: 800, fontSize: mob ? 22 : 26, color: GH.colors.white, letterSpacing: '-0.025em', margin: '0 0 36px' }}>Send Us a Message</h2>
                  <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: 20, marginBottom: 20 }}>
                    <div>
                      <label style={labelStyle}>Full Name *</label>
                      <input required value={form.name} onChange={e => set('name', e.target.value)} onFocus={() => setFocused('name')} onBlur={() => setFocused(null)} placeholder="John Smith" style={inputStyle('name')} />
                    </div>
                    <div>
                      <label style={labelStyle}>Company</label>
                      <input value={form.company} onChange={e => set('company', e.target.value)} onFocus={() => setFocused('company')} onBlur={() => setFocused(null)} placeholder="Acme Corp" style={inputStyle('company')} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: 20, marginBottom: 20 }}>
                    <div>
                      <label style={labelStyle}>Email *</label>
                      <input required type="email" value={form.email} onChange={e => set('email', e.target.value)} onFocus={() => setFocused('email')} onBlur={() => setFocused(null)} placeholder="john@company.com" style={inputStyle('email')} />
                    </div>
                    <div>
                      <label style={labelStyle}>Phone</label>
                      <input value={form.phone} onChange={e => set('phone', e.target.value)} onFocus={() => setFocused('phone')} onBlur={() => setFocused(null)} placeholder="+64 21 000 000" style={inputStyle('phone')} />
                    </div>
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <label style={labelStyle}>Service Required</label>
                    <select value={form.service} onChange={e => set('service', e.target.value)} onFocus={() => setFocused('service')} onBlur={() => setFocused(null)} style={{ ...inputStyle('service'), appearance: 'none' }}>
                      <option value="">Select a service…</option>
                      {['Construction Works','Property Maintenance','Window Cleaning','Water Blasting','Building Painting','Carpet Cleaning','Exterior Cleaning','Residential Services'].map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ marginBottom: 28 }}>
                    <label style={labelStyle}>Message *</label>
                    <textarea required value={form.message} onChange={e => set('message', e.target.value)} onFocus={() => setFocused('message')} onBlur={() => setFocused(null)} placeholder="Tell us about your project or enquiry…" rows={5} style={{ ...inputStyle('message'), resize: 'vertical', minHeight: 120 }} />
                  </div>
                  {/* File Attachments */}
                  <div style={{ marginBottom: 32 }}>
                    <label style={labelStyle}>Attachments <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: GH.colors.mutedDk }}>(optional: photos, plans, PDFs)</span></label>
                    <input ref={fileRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp,.gif,.pdf" onChange={handleFiles} style={{ display: 'none' }} aria-label="Attach files" />
                    <div onClick={() => fileRef.current?.click()} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); handleFiles({ target: { files: e.dataTransfer.files } }); }}
                      style={{ border: `2px dashed ${attachError ? GH.colors.error : GH.colors.borderMd}`, borderRadius: 2, padding: '20px 18px', textAlign: 'center', cursor: 'pointer', background: 'rgba(22,28,42,0.02)', transition: 'border-color 0.2s' }}
                      onMouseEnter={e => { if (!attachError) e.currentTarget.style.borderColor = GH.colors.accent; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = attachError ? GH.colors.error : GH.colors.borderMd; }}>
                      <div style={{ fontFamily: GH.fonts.body, fontSize: 14, color: GH.colors.mutedDk }}>Click or drag to attach files</div>
                      <div style={{ fontFamily: GH.fonts.body, fontSize: 11, color: GH.colors.grey, marginTop: 6 }}>JPG · PNG · PDF · max 5 MB each · up to 3 files</div>
                    </div>
                    {attachError && <div style={{ fontFamily: GH.fonts.body, fontSize: 12, color: GH.colors.error, marginTop: 8 }}>⚠ {attachError}</div>}
                    {attachments.length > 0 && (
                      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {attachments.map((f, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: GH.colors.accentMuted, border: `1px solid ${GH.colors.accent}33`, borderRadius: 2, padding: '8px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span style={{ fontFamily: GH.fonts.body, fontSize: 13, color: GH.colors.white }}>{f.name}</span>
                              <span style={{ fontFamily: GH.fonts.body, fontSize: 11, color: GH.colors.mutedDk }}>({(f.size / 1024).toFixed(0)} KB)</span>
                            </div>
                            <button onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: GH.colors.mutedDk, fontSize: 16, lineHeight: 1, padding: '0 4px' }} title="Remove">×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {submitError && <div style={{ fontFamily: GH.fonts.body, fontSize: 13, color: GH.colors.error, textAlign: 'center', marginBottom: 16, padding: '12px 16px', background: 'rgba(239,68,68,0.08)', borderRadius: 2 }}>{submitError}</div>}
                  <GHButton fullWidth size="lg" onClick={() => {}} disabled={submitting}>{submitting ? 'Sending...' : 'Send Message'}</GHButton>
                  <p style={{ fontFamily: GH.fonts.body, fontSize: 12, color: GH.colors.mutedDk, textAlign: 'center', marginTop: 16 }}>All enquiries are confidential.</p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}

